import React, { useState } from 'react'
import Button from '@mui/joy/Button';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import TextAreaCustom from '../../../../../common/TextAreaCustom';
import { Modal } from '@mui/base/Modal';
import { EndPoints } from '../../../../../utils/apiEndpoints';
import { StyledBackdrop } from '../../../Users/NewAccModal';
import { FieldErrors } from '../../../../../domain/helpers/Errors';
import { formatNumber } from '../../../../../common/GlobalFunctions';
import { useDarkModeStore } from '../../../../../store/useDarkModeStore';
import { useLanguageStore } from '../../../../../store/useLanguageStore';
import { ModalContent } from '../../../../../common/TopBar/Buttons/SearchTopBarModal';
import { darkModeBackgroud, darkModeBackgroudInputs } from '../../../../../utils/constants';
import { ApprovalStatus, getApprovalStatusInfo } from '../../../../../enums/approvalStatus';
import { PurchaseOrderLineInterface } from '../../../../../models/ProcurementModule/PurchateOrder/PurchaseOrderLine';
import useUpdateApprovalStatus, { UpdateStatusInput } from '../../../../../store/ReactQueries/procurements/approvalRequests/useUpdateApprovalStatus';

interface PendingRequestModalProps<T extends { [key: string]: any }> {
    form: T;
    open: {
        add: boolean;
        edit: boolean;
        view: boolean;
    };
    setOpen: React.Dispatch<React.SetStateAction<{
        add: boolean;
        edit: boolean;
        view: boolean;
    }>>;
    setForm: React.Dispatch<React.SetStateAction<T>>;
}

export const PendingRequestModal = <T extends { [key: string]: any }>({
    open,
    form,
    setOpen,
    setForm,
}: PendingRequestModalProps<T>) => {

    const { darkMode } = useDarkModeStore();
    const { language } = useLanguageStore();
    const [errors, setErrors] = useState<FieldErrors>({});
    const updateStatus = useUpdateApprovalStatus(form.id);

    const handleClose = () => {
        setOpen({
            ...open,
            add: false,
            edit: false,
            view: false
        })
    }

    const saveItem = async (status: ApprovalStatus) => {
        const { id, comment } = form;
        const result: UpdateStatusInput = { id, status, comment }
        updateStatus.mutate(result);
        handleClose();
    }

    const { Approved, Rejected, RequestForQuotation } = ApprovalStatus;

    const total = form?.purchaseOrder?.purchaseOrderLines?.reduce((sum: number, line: PurchaseOrderLineInterface) => sum + (line?.amount || 0), 0) || 0;
    const discount = form.purchaseOrder && form.purchaseOrder.discount ? form.purchaseOrder.discount : 0;

    const items: { name: string, value: string, link?: string }[] = [
        { name: 'Name', value: `${form?.author?.firstname ?? ''} ${form?.author?.lastname ?? ''}` },
        { name: 'PurchaseOrder', value: `${form?.purchaseOrder?.code ?? ''}`, link: `${EndPoints.PURCHASEORDERS}/edit/${form.purchaseOrderId}` },
        { name: 'PurchaseOrderType', value: `${form?.purchaseOrder?.purchaseOrderType?.title ?? ''}` },
        { name: 'Description', value: `${form?.purchaseOrder?.description ?? ''}` },
        { name: 'Vendor', value: `${form?.purchaseOrder?.vendor?.name ?? ''}` },
        { name: 'TotalInEuro', value: `${formatNumber(total - discount)} €` },
        { name: 'RequestForQuotation', value: form?.purchaseOrder?.requestForQuotation.toString() },
    ];

    return (
        <Modal
            open={true}
            onClose={handleClose}
            slots={{ backdrop: StyledBackdrop }}
        >
            <ModalContent
                sx={{
                    width: 850,
                    top: '50%',
                    left: '50%',
                    zIndex: 1300,
                    position: 'fixed',
                    pointerEvents: "auto",
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: darkMode ? darkModeBackgroud : 'white',
                }}
            >
                <div>
                    <div className="d-flex flex-row align-items-center ps-3 pe-3 pt-3">
                        <h4 className={`${darkMode ? 'text-white' : 'text-muted'} text-truncate`} style={{ maxWidth: 800 }}>
                            {language['PurchaseRequest']}
                        </h4>
                        <IconButton
                            aria-label="Close Modal"
                            title="Close Modal"
                            size="small"
                            color={darkMode ? "info" : "default"}
                            className="ms-auto"
                            onClick={handleClose}
                        >
                            <ClearIcon />
                        </IconButton>
                    </div>

                    <hr className={`${darkMode && 'text-white opacity-100'} m-0 mt-2 p-0`} />
                    <div className="table-responsive p-3" style={{ maxHeight: 600, overflow: 'auto' }}>
                        <table className="table table-bordered w-100">
                            <tbody>
                                {items.map(({ name, value, link }, index) => (
                                    <tr key={index} className="align-middle">
                                        <td
                                            className={`${darkMode ? 'text-white' : 'text-secondary'} fw-bold pt-3 pb-3`}
                                            style={{
                                                backgroundColor: darkMode ? darkModeBackgroudInputs : '#f8f9fa',
                                                width: '35%',
                                            }}
                                        >
                                            {language[name] ?? name}
                                        </td>

                                        <td
                                            className={`${darkMode ? 'text-white' : 'text-secondary'} pt-3 pb-3 ps-3`}
                                            style={{
                                                width: '65%',
                                                backgroundColor: darkMode ? darkModeBackgroudInputs : 'initial',
                                            }}
                                        >
                                            {
                                                name === 'RequestForQuotation'
                                                    ? <span>{value === 'false' ? language['No'] : language['Yes']}</span>
                                                    : link
                                                        ? <a href={link} target="_blank" rel="noopener noreferrer">
                                                            {value}
                                                        </a>
                                                        : value
                                            }
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className='mb-2 mt-2 p-3 pt-2'>
                        <label
                            htmlFor='comment'
                            className={`${darkMode ? 'text-white' : 'text-secondary'} fw-bold`}
                        >
                            {language['Comment'] ?? 'Comment'}
                        </label>
                        <TextAreaCustom
                            form={form}
                            view={false}
                            name='comment'
                            errors={errors}
                            setForm={setForm}
                        />
                    </div>

                    <hr className={`${darkMode && 'text-white opacity-100'} m-0 p-0`} />

                    <div className="d-flex align-items-center justify-content-end p-2 mt-2 gap-2">
                        {
                            [Approved, Rejected, RequestForQuotation].map(item => {
                                const status = getApprovalStatusInfo(item);
                                return <Button
                                    key={item}
                                    className="w-25"
                                    onClick={() => saveItem(item)}
                                    style={{
                                        color: 'white',
                                        background: status.color,
                                    }}
                                >
                                    {language[status.name] ?? status.name}
                                </Button>
                            })
                        }
                    </div>
                </div>
            </ModalContent>
        </Modal >
    );
}
export default PendingRequestModal